import React, { useState, useMemo, useEffect, useRef } from "react";

import SoftBox from "../../components/SoftBox";
import LightSelect from "./LightSelect";
import LoadingScreen from "../../components/LoadingScreen";
import { formatNumber } from "../../helpers/formatter";
import { formatKpiPercent } from "../../helpers/scoreAggregate";

/* Distinct line colours, assigned per metric index within a workspace. */
const PALETTE = ["#D4537E", "#378ADD", "#1D9E75", "#BA7517", "#7F77DD", "#D85A30"];

/* Chart geometry (SVG user units — scales responsively via viewBox). */
const W = 1040;
const H = 320;
const PADL = 54;
const PADR = 14;
const PADT = 10;
const PADB = 26;
const PLOTW = W - PADL - PADR;
const PLOTH = H - PADT - PADB;
const GRID = [0, 1, 2, 3, 4];

const niceMax = (v) => {
  if (v <= 0) return 10;
  const p = 10 ** Math.floor(Math.log10(v));
  let n = Math.ceil(v / p) * p;
  if (n < v * 1.1) n += p / 2;
  return n;
};
const niceMaxPct = (v) => {
  if (v <= 50) return 50;
  if (v <= 100) return 100;
  if (v <= 150) return 150;
  if (v <= 200) return 200;
  return Math.ceil(v / 50) * 50;
};
const fmtValue = (meta, value) => (meta?.kind === "%" ? formatKpiPercent(meta, value) : formatNumber(value, meta?.kind));
const fmtAxis = (v, kind) => {
  if (kind === "$") return v >= 1000 ? `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K` : `$${Math.round(v)}`;
  return Math.round(v * 10) / 10;
};

const MetricPerformance = ({ trend }) => {
  const months = trend?.months || [];
  const boards = useMemo(
    () => (trend?.workspaces || []).filter((w) => w.metrics?.length),
    [trend],
  );

  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    if (boards.length && !boards.some((b) => b.workspaceId === activeId)) {
      setActiveId(boards[0].workspaceId);
    }
  }, [boards, activeId]);

  const active = boards.find((b) => b.workspaceId === activeId) || boards[0];
  const options = useMemo(
    () => boards.map((b) => ({ value: b.workspaceId, label: b.workspace })),
    [boards],
  );
  const metrics = useMemo(
    () => (active?.metrics || []).map((m, i) => ({ ...m, color: PALETTE[i % PALETTE.length] })),
    [active],
  );

  const [view, setView] = useState("all"); // "all" | metric index
  const [chartType, setChartType] = useState("line");
  const [hover, setHover] = useState(null);
  useEffect(() => { setView("all"); }, [activeId]);

  const isAll = view === "all";
  const focus = !isAll ? metrics[view] : null;

  const yMax = useMemo(() => {
    if (isAll) {
      let mx = 10;
      metrics.forEach((m) => (m.series || []).forEach((v) => { if (v != null && v > mx) mx = v; }));
      return niceMaxPct(mx);
    }
    let mx = 1;
    (focus?.real || []).forEach((v) => { if (v != null && v > mx) mx = v; });
    return niceMax(mx);
  }, [isAll, metrics, focus]);

  const xAt = (i) => PADL + PLOTW * (months.length > 1 ? i / (months.length - 1) : 0);
  const yAt = (v) => PADT + PLOTH * (1 - v / yMax);
  const monthCols = months.map((mo, i) => ({ mo, i, x: xAt(i) }));

  const svgRef = useRef(null);
  const onMove = (e) => {
    const svg = svgRef.current;
    if (!svg || months.length === 0) return;
    const r = svg.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * W;
    let i = Math.round(((x - PADL) / PLOTW) * (months.length - 1));
    i = Math.max(0, Math.min(months.length - 1, i));
    setHover(i);
  };

  // Series to plot for the current view.
  let plotSeries = [];
  if (isAll) {
    plotSeries = metrics.map((m) => ({ id: m.kpiId, color: m.color, data: m.series }));
  } else if (focus) {
    plotSeries = [{ id: focus.kpiId, color: focus.color, data: focus.real }];
  }

  const linePath = (data) =>
    data
      .map((v, i) => (v == null ? null : `${i === 0 ? "M" : "L"}${xAt(i).toFixed(1)} ${yAt(v).toFixed(1)}`))
      .filter(Boolean)
      .join(" ");

  const areaPath = (data) => {
    const pts = data.map((v, i) => (v == null ? null : `L${xAt(i).toFixed(1)} ${yAt(v).toFixed(1)}`)).filter(Boolean);
    if (!pts.length) return "";
    return `M${xAt(0).toFixed(1)} ${yAt(0).toFixed(1)} ${pts.join(" ")} L${xAt(months.length - 1).toFixed(1)} ${yAt(0).toFixed(1)} Z`;
  };

  return (
    <SoftBox
      borderRadius="lg"
      sx={{
        p: "16px 20px 16px",
        background: "#ffffff",
        border: "1px solid #EAECF0",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header — title + workspace + chart type */}
      <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
        <span style={{ fontFamily: "DM Sans", fontSize: "18px", fontWeight: 600, letterSpacing: "-0.01em", color: "#0F172A", lineHeight: 1.2 }}>
          Workspace performance overview
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {options.length > 1 && (
            <LightSelect options={options} value={active?.workspaceId} width={148} height={30} fontSize={12.5} radius={8} onChange={(o) => setActiveId(o.value)} />
          )}
          <span style={{ display: "inline-flex", border: "1px solid #EEF1F5", borderRadius: 8, overflow: "hidden" }}>
            {[{ key: "bar", label: "Bar" }, { key: "line", label: "Line" }].map((t, i) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setChartType(t.key)}
                style={{
                  height: 30, padding: "0 11px", fontFamily: "DM Sans", fontSize: 12,
                  fontWeight: chartType === t.key ? 600 : 500,
                  color: chartType === t.key ? "var(--gmb-primary, #1B598E)" : "#8A94A6",
                  background: chartType === t.key ? "var(--gmb-primary-soft, rgba(27,89,142,0.08))" : "#fff",
                  border: "none", borderRight: i === 0 ? "1px solid #EEF1F5" : "none", cursor: "pointer",
                }}
              >
                {t.label}
              </button>
            ))}
          </span>
        </span>
      </SoftBox>

      {!trend && (
        <SoftBox sx={{ height: "340px" }} className="w-full flex justify-center items-center">
          <LoadingScreen height="150px" logoHeight="150px" />
        </SoftBox>
      )}

      {trend && !boards.length && (
        <SoftBox sx={{ height: "340px" }} className="w-full flex justify-center items-center">
          <span style={{ fontFamily: "DM Sans", fontSize: 12.5, color: "#98A2B3" }}>No metric data yet.</span>
        </SoftBox>
      )}

      {trend && active && (
        <>
          {/* Selector strip — All metrics + one cell per metric */}
          <SoftBox
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))`,
              mt: "12px",
              border: "1px solid #EEF1F5",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            {metrics.map((m, i) => {
              const on = view === i;
              const up = (m.deltaPct ?? 0) >= 0;
              return (
                <div
                  key={m.kpiId}
                  onClick={() => setView(view === i ? "all" : i)}
                  title={on ? "Click again to show all metrics" : `Show only ${m.title}`}
                  style={{ padding: "12px 15px", borderRight: i === metrics.length - 1 ? "none" : "1px solid #EEF1F5", cursor: "pointer", position: "relative", background: on ? "#FBFCFE" : "transparent" }}
                >
                  {on && <span style={{ position: "absolute", left: 0, top: 0, right: 0, height: 3, background: m.color }} />}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: m.color, flexShrink: 0 }} />
                    <span style={{ fontFamily: "DM Sans", fontSize: 12, fontWeight: 500, color: on ? "#101828" : "#475467", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.title}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 7, marginTop: 8 }}>
                    <span style={{ fontFamily: "DM Sans", fontSize: 17, fontWeight: 600, color: "#101828", fontVariantNumeric: "tabular-nums" }}>
                      {fmtValue(m, m.total)}
                    </span>
                    {m.deltaPct != null && (
                      <span style={{ fontFamily: "DM Sans", fontSize: 11, fontWeight: 600, padding: "1px 6px", borderRadius: 6, fontVariantNumeric: "tabular-nums", background: up ? "#E7F5EC" : "#FBEAEA", color: up ? "#1D7A46" : "#B42318" }}>
                        {up ? "+" : ""}{Math.round(m.deltaPct)}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </SoftBox>

          {/* Chart */}
          <SoftBox sx={{ position: "relative", mt: "12px" }}>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="xMidYMid meet"
              style={{ display: "block", width: "100%", height: "auto" }}
              onMouseMove={onMove}
              onMouseLeave={() => setHover(null)}
            >
              {GRID.map((g) => {
                const val = (yMax * g) / 4;
                const y = yAt(val);
                return (
                  <g key={g}>
                    <line x1={PADL} y1={y} x2={W - PADR} y2={y} stroke="#EEF1F5" strokeWidth="1" />
                    <text x={PADL - 10} y={y + 4} textAnchor="end" fontSize="11" fill="#98A2B3" fontFamily="DM Sans">
                      {isAll ? `${Math.round(val)}%` : fmtAxis(val, focus?.kind)}
                    </text>
                  </g>
                );
              })}
              {monthCols.map((c) => (
                <text key={c.mo} x={c.x} y={H - 10} textAnchor="middle" fontSize="11" fill="#98A2B3" fontFamily="DM Sans">
                  {c.mo}
                </text>
              ))}

              {!isAll && focus && chartType === "line" && (
                <path d={areaPath(focus.real)} fill={focus.color} opacity="0.10" />
              )}

              {chartType === "line"
                ? plotSeries.map((s) => (
                    <path key={s.id} d={linePath(s.data)} fill="none" stroke={s.color} strokeWidth={isAll ? 1.5 : 2} strokeLinejoin="round" strokeLinecap="round" />
                  ))
                : monthCols.map((c) => {
                    const slot = PLOTW / months.length;
                    const bw = Math.min(isAll ? 9 : 26, (slot * (isAll ? 0.62 : 0.5)) / Math.max(plotSeries.length, 1));
                    const groupW = bw * plotSeries.length;
                    const startX = c.x - groupW / 2;
                    return (
                      <g key={c.mo}>
                        {plotSeries.map((s, k) => {
                          const v = s.data?.[c.i];
                          if (v == null) return null;
                          const yTop = yAt(v);
                          return <rect key={s.id} x={(startX + k * bw).toFixed(1)} y={yTop.toFixed(1)} width={(bw - 1.5).toFixed(1)} height={Math.max(0, yAt(0) - yTop).toFixed(1)} rx="2" fill={s.color} />;
                        })}
                      </g>
                    );
                  })}

              {hover != null && (
                <line x1={xAt(hover)} y1={PADT} x2={xAt(hover)} y2={PADT + PLOTH} stroke="#101828" strokeWidth="1" opacity="0.28" />
              )}
            </svg>

            {hover != null && (
              <div
                style={{
                  position: "absolute",
                  top: 6,
                  left: `calc(${(xAt(hover) / W) * 100}% ${xAt(hover) > W / 2 ? "- 160px" : "+ 14px"})`,
                  pointerEvents: "none",
                  background: "#fff",
                  border: "1px solid #EAECF0",
                  borderRadius: 10,
                  boxShadow: "0 10px 28px rgba(16,24,40,0.12)",
                  padding: "8px 11px",
                  minWidth: 130,
                  fontFamily: "DM Sans",
                  zIndex: 5,
                  whiteSpace: "nowrap",
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", color: "#98A2B3", textTransform: "uppercase", marginBottom: 5 }}>
                  {isAll ? months[hover] : `${focus.title} · ${months[hover]}`}
                </div>
                {isAll
                  ? metrics.map((m) => (
                      <div key={m.kpiId} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, margin: "3px 0" }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: m.color, flexShrink: 0 }} />
                        <span style={{ color: "#667085" }}>{m.title}</span>
                        <span style={{ color: "#101828", fontWeight: 600, marginLeft: "auto", fontVariantNumeric: "tabular-nums" }}>
                          {m.series?.[hover] == null ? "–" : `${Math.round(m.series[hover])}%`}
                        </span>
                      </div>
                    ))
                  : (
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#101828", fontVariantNumeric: "tabular-nums" }}>
                      {focus.real?.[hover] == null ? "–" : fmtValue(focus, focus.real[hover])}
                    </div>
                  )}
              </div>
            )}
          </SoftBox>
        </>
      )}
    </SoftBox>
  );
};

export default MetricPerformance;
